import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { ROUTES } from '@/config/routes';
import type { UserRole } from '@/types/user';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  /** The content to render if authorised. */
  children: ReactNode;
  /** Required role(s). If omitted, any authenticated user can access. */
  requiredRole?: UserRole | UserRole[];
}

/**
 * Auth gate and role-check wrapper.
 *
 * Redirects unauthenticated users to the login page.
 * Optionally checks that the user has one of the required roles.
 * Shows a "not authorised" message if the role check fails.
 */
export function ProtectedRoute({
  children,
  requiredRole,
}: ProtectedRouteProps) {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();

  // While checking auth state, show nothing (the App-level loader handles this)
  if (loading) {
    return null;
  }

  // Not signed in — redirect to login, preserving the intended destination
  if (!user) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Role check
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-foreground">
            Access Restricted
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-md">
            You do not have permission to access this page.
            Contact your administrator if you believe this is an error.
          </p>
          <a
            href={ROUTES.DASHBOARD}
            className="mt-4 inline-block text-sm font-medium text-primary hover:underline"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
